import { useEffect, useState, MouseEvent, useCallback, memo } from 'react';
import { css } from '@emotion/react';
import { ObjectTypeCallRecording } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import {
  CallRecordingSetting,
  UpdateCallRecordingRequest,
} from '@weave/schema-gen-ts/dist/schemas/phone/call-config/v2/call_config.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { useQueryClient } from 'react-query';
import { CallRecordingApi } from '@frontend/api-call-recording';
import { phoneAuditsQueryKeys } from '@frontend/api-phone-audits';
import { useCallIntelFeatureAccess } from '@frontend/call-intel-core';
import { GlobalBannerView } from '@frontend/global-info-banner';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  ConfirmationModal,
  Heading,
  RadioField,
  SwitchField,
  Text,
  WarningBadgeIcon,
  useForm,
  useModalControl,
  useAlert,
  InlineNotification,
} from '@frontend/design-system';
import { SeeHistoryButtonWithCallout } from '../../components/common/see-history-button-with-callout';
import { DetailsHeader } from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
  useHistoryTrayShouldOverlap,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { PhoneSystemHistoryTrackingIds } from '../../components/phone-system-history/utils/tracking-ids';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { WeaveSupportLink } from '../../components/weave-support-link';
import { queryKeys } from '../../query-keys';
import {
  callRecordingRadioFieldStyle,
  emptyCallRecordingSectionStyle,
  radioFieldContainerStyle,
  radioFieldTextStyle,
  radioFieldDescriptionStyle,
  formContainerStyle,
  confirmationModalTextStyle,
} from './call-recording-styles';

export const CallRecordingSettings = () => {
  const { t } = useTranslation('phone');
  const queryClient = useQueryClient();
  const alert = useAlert();
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const singleLocationId = selectedLocationIds[0];

  const { hasCallIntelFeature: isCallIntelActive } = useCallIntelFeatureAccess();

  const location = accessibleLocationData[singleLocationId];
  const tenantId = location?.phoneTenantId || '';
  const isUnifyLocation = location.locationType === 'unify';

  const { associatedLocationIds } = usePhoneSettingsShallowStore('associatedLocationIds');
  const selectedLocations = (associatedLocationIds ?? []).map((id) => accessibleLocationData[id]).filter(Boolean);
  const { hasPhoneSystemHistoryAccess } = useHasPhoneSettingsAccess({ selectedLocations });

  const callRecordingsQuery = useScopedQuery({
    queryKey: queryKeys.listCallRecording(tenantId),
    queryFn: () => CallRecordingApi.GetCallRecording({ tenantId: tenantId }),
  });

  const { mutate: updateCallRecording } = useMutation(
    (payload: UpdateCallRecordingRequest) => {
      if (!payload.tenantId) throw new Error('Tenant ID is required to update call recording');
      return CallRecordingApi.UpdateCallRecording(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate(query) {
            return (
              !!query.queryKey &&
              !!Array.isArray(query.queryKey) &&
              queryKeys.listCallRecording(tenantId).every((keyPart) => query.queryKey.includes(keyPart))
            );
          },
          exact: false,
        });
        queryClient.invalidateQueries(phoneAuditsQueryKeys.searchAuditEventsByTenant(tenantId));
        alert.success(t('Call Recording updated successfully'));
      },
      onError: () => {
        alert.error(t('Failed to update call recording'));
      },
    }
  );

  const { modalProps, openModal, closeModal } = useModalControl();
  const {
    modalProps: disableModalProps,
    openModal: openDisableModal,
    closeModal: closeDisableModal,
  } = useModalControl();
  const {
    modalProps: inboundCallsModalProps,
    openModal: openInboundCallsModal,
    closeModal: closeInboundCallsModal,
  } = useModalControl();
  const {
    modalProps: outboundCallsModalProps,
    openModal: openOutboundCallsModal,
    closeModal: closeOutboundCallsModal,
  } = useModalControl();

  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');
  const historyTray = useModalControl();
  const shouldOverlapHistoryTray = useHistoryTrayShouldOverlap();
  const isSquishedMode = historyTray.modalProps.show && !shouldOverlapHistoryTray;

  const { formProps, getFieldProps, seedValues } = useForm({
    computeChangedValues: true,
    fields: {
      showRecording: { type: 'switch' },
      selectRecording: { type: 'radio' },
    },
  });

  const showRecordingProps = getFieldProps('showRecording');
  const selectRecordingProps = getFieldProps('selectRecording');
  const [showRibbon, setShowRibbon] = useState(true);
  const ribbonClosedKey = `ribbonClosed-${location.id}`;

  useEffect(() => {
    const isRibbonClosed = sessionStorage.getItem(ribbonClosedKey) === 'true';
    const isEnabled = callRecordingsQuery.data?.setting !== CallRecordingSetting.NONE;
    seedValues({
      showRecording: isEnabled,
      selectRecording: callRecordingsQuery.data?.setting,
    });
    setShowRibbon(!isRibbonClosed);
  }, [callRecordingsQuery.data]);

  // sessionStorage is cleared upon page reload/refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.clear();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSwitchChange = () => {
    if (showRecordingProps.value) {
      if (isCallIntelActive) {
        openDisableModal();
      } else {
        updateCallRecording({ tenantId: tenantId, setting: CallRecordingSetting.NONE });
      }
    } else {
      openModal();
    }
  };

  const handleRadioChange = (value: CallRecordingSetting) => (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    updateCallRecording({ tenantId, setting: value });
  };

  const handleConfirm = () => {
    updateCallRecording({ tenantId: tenantId, setting: CallRecordingSetting.BOTH });
    closeModal();
  };

  const handleDisableConfirm = () => {
    updateCallRecording({ tenantId: tenantId, setting: CallRecordingSetting.NONE });
    closeDisableModal();
  };

  const handleInboundCallsConfirm = () => {
    handleRadioChange(CallRecordingSetting.INBOUND)({ preventDefault: () => {} } as any);
    closeInboundCallsModal();
  };

  const handleOutboundCallsConfirm = () => {
    handleRadioChange(CallRecordingSetting.OUTBOUND)({ preventDefault: () => {} } as any);
    closeOutboundCallsModal();
  };

  const handleRibbonClose = useCallback(() => {
    setShowRibbon(false);
    sessionStorage.setItem(ribbonClosedKey, 'true');
  }, [ribbonClosedKey, setShowRibbon]);

  const overlayBlockerStyle = css`
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      background: transparent;
      cursor: pointer;
    }
  `;

  return (
    <>
      {showRibbon && isUnifyLocation && <CallRecordingNotificationRibbon onClose={handleRibbonClose} />}
      <PhoneSettingsPage
        title={t('Call Recording')}
        subtitle={t('Please indicate whether you want to record your inbound and outbound calls.')}
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Call Recording'), to: '/phone/call-recording' },
        ]}
        maxWidth={700}
        action={
          phoneSystemHistoryEnabled &&
          hasPhoneSystemHistoryAccess && (
            <SeeHistoryButtonWithCallout
              featureId='phone-system-history-callout-call-recording'
              isActive={historyTray.modalProps.show}
              onClick={historyTray.modalProps.show ? historyTray.closeModal : historyTray.openModal}
              startDate='2026-07-07'
              endDate='2026-09-22'
              trackingIdPrefix={PhoneSystemHistoryTrackingIds.seeHistoryButtons.callRecording}
            />
          )
        }
        css={css`
          width: ${isSquishedMode ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <form {...formProps}>
          <div css={formContainerStyle}>
            <section css={emptyCallRecordingSectionStyle}>
              <Heading level={3}>{t('Call Recording Details')}</Heading>
              <SwitchField
                {...showRecordingProps}
                name='call recording'
                label={t('Call Recording')}
                labelPlacement='left'
                value={showRecordingProps.value}
                onChange={handleSwitchChange}
                trackingId='phone-callRecording-switch'
              />
            </section>
            {isCallIntelActive && !showRecordingProps.value && (
              <InlineNotification status='error' size='large'>
                <Text size='medium'>
                  {t(
                    'Calls are not being transcribed or analyzed in Call Intelligence. To generate data and insights, Call Intelligence requires Call Recording to be enabled.'
                  )}
                </Text>
              </InlineNotification>
            )}
            {callRecordingsQuery.data?.setting !== CallRecordingSetting.NONE && showRecordingProps.value && (
              <>
                <section
                  css={css`
                    display: flex;
                    align-items: center;
                    background: ${theme.colors.primary5};
                    border: 1px solid ${theme.colors.primary50};
                    border-radius: ${theme.borderRadius.medium};
                    padding: ${theme.spacing(2)};
                  `}
                >
                  <WarningBadgeIcon
                    color='primary'
                    css={{ marginRight: theme.spacing(2), transform: 'rotate(180deg)' }}
                  />
                  <Text size='medium'>
                    <Trans t={t}>
                      To enable Call Recording notices, please contact <WeaveSupportLink />.
                    </Trans>
                  </Text>
                </section>
                <Text weight='bold'>{t('Select Recording Rules')}</Text>
                <RadioField {...selectRecordingProps} css={callRecordingRadioFieldStyle}>
                  <div css={radioFieldContainerStyle} onClick={handleRadioChange(CallRecordingSetting.BOTH)}>
                    <RadioField.Radio value={CallRecordingSetting.BOTH} trackingId='phone-callRecording-radio-AllCalls'>
                      <div>
                        <Text weight='bold' css={radioFieldTextStyle}>
                          {t('All Calls')}
                        </Text>
                        <Text css={radioFieldDescriptionStyle}>{t('Record both inbound and outbound calls')}</Text>
                      </div>
                    </RadioField.Radio>
                  </div>
                  <div
                    css={[
                      radioFieldContainerStyle,
                      isCallIntelActive && selectRecordingProps.value !== CallRecordingSetting.INBOUND
                        ? overlayBlockerStyle
                        : undefined,
                    ]}
                    onClick={(event) =>
                      isCallIntelActive && selectRecordingProps.value !== CallRecordingSetting.INBOUND
                        ? openInboundCallsModal()
                        : handleRadioChange(CallRecordingSetting.INBOUND)(event)
                    }
                  >
                    <RadioField.Radio
                      value={CallRecordingSetting.INBOUND}
                      trackingId='phone-callRecording-radio-InboundCall'
                      disabled={isCallIntelActive && selectRecordingProps.value !== CallRecordingSetting.INBOUND}
                    >
                      <div>
                        <Text weight='bold' css={radioFieldTextStyle}>
                          {t('Inbound Calls')}
                        </Text>
                        <Text css={radioFieldDescriptionStyle}>{t('Record only inbound calls')}</Text>
                      </div>
                    </RadioField.Radio>
                  </div>
                  <div
                    css={[
                      radioFieldContainerStyle,
                      isCallIntelActive && selectRecordingProps.value !== CallRecordingSetting.OUTBOUND
                        ? overlayBlockerStyle
                        : undefined,
                    ]}
                    onClick={(event) =>
                      isCallIntelActive && selectRecordingProps.value !== CallRecordingSetting.OUTBOUND
                        ? openOutboundCallsModal()
                        : handleRadioChange(CallRecordingSetting.OUTBOUND)(event)
                    }
                  >
                    <RadioField.Radio
                      value={CallRecordingSetting.OUTBOUND}
                      trackingId='phone-callRecording-radio-OutboundCall'
                    >
                      <div>
                        <Text weight='bold' css={radioFieldTextStyle}>
                          {t('Outbound Calls')}
                        </Text>
                        <Text css={radioFieldDescriptionStyle}>{t('Record only outbound calls')}</Text>
                      </div>
                    </RadioField.Radio>
                  </div>
                </RadioField>
                {isCallIntelActive && selectRecordingProps.value === CallRecordingSetting.INBOUND && (
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Icon name='info' color='subdued' />
                    <Text size='medium' color='subdued'>
                      <Trans>
                        Call Intelligence requires calls to be recorded for transcription and analysis. Only{' '}
                        <strong>inbound</strong> calls are being transcribed and analyzed.
                      </Trans>
                    </Text>
                  </div>
                )}
                {isCallIntelActive && selectRecordingProps.value === CallRecordingSetting.OUTBOUND && (
                  <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                    <Icon name='info' color='subdued' />
                    <Text size='medium' color='subdued'>
                      <Trans>
                        Call Intelligence requires calls to be recorded for transcription and analysis. Only{' '}
                        <strong>outbound</strong> calls are being transcribed and analyzed.
                      </Trans>
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        </form>
        <ConfirmationModal
          {...modalProps}
          maxWidth={600}
          title='Call Recording Agreement'
          message={
            <div>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Weave provides you the technology to record telephone conversations. Weave does not provide legal advice about such recordings.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Under the laws of some jurisdictions (California, Connecticut, Florida, Hawaii, Illinois, Maryland, Massachusetts, Montana, Nevada, New Hampshire, Pennsylvania, and Washington, and possibly others), recording telephone conversations requires the consent of both parties being recorded.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Please note that although outbound calls are recorded, recipients will not receive an alert notifying them of the recording. In the absence of such a notification, we strongly recommend that you and your staff inform call recipients about the recording to ensure they are aware of the associated risks.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'By proceeding and clicking "I Agree" below, you represent that you have obtained that consent or that applicable laws do not require that consent.'
                )}
              </Text>
              <Text>{t('You must click "I Agree" to enable call recording functionality.')}</Text>
            </div>
          }
          confirmLabel={t('I Agree')}
          onConfirm={handleConfirm}
          cancelLabel={t('I Disagree')}
          confirmTrackingId='phone-callRecording-modal-confirm'
          cancelTrackingId='phone-callRecording-modal-cancel'
        />
        <ConfirmationModal
          {...disableModalProps}
          destructive
          maxWidth={600}
          title='Disable Call Recording?'
          message={
            <div>
              <div css={confirmationModalTextStyle}>
                <Trans t={t}>
                  Disabling Call Recording will stop all future call transcriptions and analysis in Call Intelligence.{' '}
                  <strong>No new call data will appear in Call Intelligence once Call Recording is turned off.</strong>
                </Trans>
              </div>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Call Intelligence cannot retroactively transcribe or analyze calls that were not recorded in Weave. Any existing data will remain available.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>{t('Are you sure you want to disable Call Recording?')}</Text>
            </div>
          }
          confirmLabel={t('Yes, Disable')}
          onConfirm={handleDisableConfirm}
          cancelLabel={t('No, Cancel')}
          confirmTrackingId='phone-callRecording-modal-confirm-disable'
          cancelTrackingId='phone-callRecording-modal-cancel-disable'
        />
        <ConfirmationModal
          {...inboundCallsModalProps}
          maxWidth={600}
          title='Record Only Inbound Calls?'
          message={
            <div>
              <div css={confirmationModalTextStyle}>
                <Trans t={t}>
                  Recording only inbound calls will stop all future call transcriptions and analysis for{' '}
                  <em>outbound</em> calls in Call Intelligence. {''}
                  <strong>
                    No new Call Intelligence data will appear for inbound calls once this setting is changed.
                  </strong>
                </Trans>
              </div>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Call Intelligence cannot retroactively transcribe or analyze calls that were not recorded in Weave. Any existing data will remain available.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>{t('Are you sure you want to record only inbound calls?')}</Text>
            </div>
          }
          confirmLabel={t('Yes, Update Setting')}
          onConfirm={handleInboundCallsConfirm}
          cancelLabel={t('No, Cancel')}
          confirmTrackingId='phone-callRecording-modal-confirm-inbound-calls'
          cancelTrackingId='phone-callRecording-modal-cancel-inbound-calls'
        />
        <ConfirmationModal
          {...outboundCallsModalProps}
          maxWidth={600}
          title='Record Only Outbound Calls?'
          message={
            <div>
              <div css={confirmationModalTextStyle}>
                <Trans t={t}>
                  Recording only outbound calls will stop all future call transcriptions and analysis for{' '}
                  <em>inbound</em> calls in Call Intelligence. {''}
                  <strong>
                    No new Call Intelligence data will appear for inbound calls once this setting is changed.
                  </strong>
                </Trans>
              </div>
              <Text css={confirmationModalTextStyle}>
                {t(
                  'Call Intelligence cannot retroactively transcribe or analyze calls that were not recorded in Weave. Any existing data will remain available.'
                )}
              </Text>
              <Text css={confirmationModalTextStyle}>{t('Are you sure you want to record only outbound calls?')}</Text>
            </div>
          }
          confirmLabel={t('Yes, Update Setting')}
          onConfirm={handleOutboundCallsConfirm}
          cancelLabel={t('No, Cancel')}
          confirmTrackingId='phone-callRecording-modal-confirm-outbound-calls'
          cancelTrackingId='phone-callRecording-modal-cancel-outbound-calls'
        />
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={historyTray}
        tenantId={tenantId}
        callObjectType={ObjectTypeCallRecording}
        HeaderComponent={<DetailsHeader objectType={ObjectTypeCallRecording} onClose={historyTray.closeModal} />}
      />
    </>
  );
};

interface CallRecordingNotificationRibbonProps {
  onClose: () => void;
}

const CallRecordingNotificationRibbon = memo<CallRecordingNotificationRibbonProps>(
  function CallRecordingNotificationRibbon({ onClose }) {
    const { t } = useTranslation('phone');
    const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

    const bannerMessage = t('Call recording settings apply to all calls in {{tenantName}} locations.', {
      tenantName: settingsTenantLocation?.name,
    });

    return (
      <GlobalBannerView
        type='info'
        title={t('Call recording settings affect all locations')}
        message={bannerMessage}
        onDismiss={onClose}
        trackingId='phone-callRecording-ribbon-close'
      />
    );
  }
);
